import React, { useRef, useState, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { TextInputProps } from '../../../types/common-types'
import { generateId, requestForm } from '../../../utils/helpers'
import accountForm from '../../../content/account-form'
import styles from './FileInput.module.scss'
import { State } from '../../../state-actions-reducers'
import cn from 'classnames'
import LoadingScreen from '../../../Layouts/LoadingScreen/LoadingScreen'
import { getSavedFormId } from '../../../local-storage'

interface FileInputProps extends TextInputProps {
  isSingleMode?: boolean
  documentType?: string
  singleType?: string
  imageErrors?: Array<{ type: string; status: number }>
}

function FileInput({
  disabled,
  lang,
  content,
  onChange,
  value,
  className,
  valid,
  required,
  focus,
  url,
  autorization,
  target = [],
  dispatch,
  documentType,
  singleType,
  onBlur,
  imageErrors = [],
  isSingleMode = false,
  ...props
}: FileInputProps) {
  const { setValue } = useFormContext<State>()
  const idRef = useRef(generateId(16))
  const inputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (focus && inputRef.current) {
      inputRef.current.focus()
    }
  }, [focus])

  const [inputValue, setInputValue] = useState(
    value?.replace('C:\\fakepath\\', '') || content.placeholder[lang]
  )

  useEffect(() => {
    setInputValue(
      value?.replace('C:\\fakepath\\', '') || content.placeholder[lang]
    )
  }, [lang, value])

  const [errorResponse, setErrorResponse] = useState('')
  const [loading, setLoading] = useState(false)
  const token = getSavedFormId(window.localStorage)

  function isHeicFile(file: File) {
    return /image\/hei[cf]/i.test(file.type) || /\.hei[cf]$/i.test(file.name)
  }

  async function convertHeicToJpeg(file: File) {
    const { heicTo } = await import('heic-to')
    const jpegBlob = await heicTo({ blob: file, type: 'image/jpeg', quality: 0.9 })
    return new File([jpegBlob], file.name.replace(/\.hei[cf]$/i, '.jpg'), { type: 'image/jpeg' })
  }

  function getTranslateError(errorsStatus: number) {
    return errorsStatus === 415
      ? accountForm.uploadErrorId1[lang]
      : errorsStatus === 413
      ? accountForm.uploadErrorId0[lang]
      : accountForm.uploadErrorId2[lang]
  }

  function handleErrorUpload(err: any) {
    const translateError = getTranslateError(err.errorsStatus)
    setErrorResponse(translateError)
    setValue('imageError', !imageErrors?.find((e) => e.type === documentType)
      ? [...(imageErrors ?? []), { type: documentType, status: err.errorsStatus }]
      : imageErrors.map((image) => {
          return image.type === documentType
            ? { ...image, status: err.errorsStatus }
            : image
        }), { shouldDirty: true })
  }

  async function handleUpload(e: React.ChangeEvent<HTMLInputElement>) {
    setLoading(true)
    const value = e.target.value
    const formData = new FormData()
    let file = e.target.files ? e.target.files[0] : null

    if (file && isHeicFile(file)) {
      try {
        file = await convertHeicToJpeg(file)
      } catch {
        setErrorResponse(accountForm.uploadErrorHeic[lang])
        setLoading(false)
        return
      }
    }

    try {
      formData.append('File', file ?? '')

      const data = await requestForm(
        url,
        formData,
        {
          authorization: `Bearer ${token}`,
        },
        true,
        'Error while token generation'
      )

      if (data.errorsStatus || !data.url) {
        handleErrorUpload(data)
        return
      }

      setValue('imageError', imageErrors?.filter((e) => e.type !== documentType) ?? [], { shouldDirty: true })

      setErrorResponse('')

      if (isSingleMode) {
        // НОВЫЙ РЕЖИМ
        const newFileData = {
          fileName: data.url,
          originalFileName: value.replace('C:\\fakepath\\', ''),
          type: 0,
        }
        onChange(newFileData, e)
      } else {
        // СТАНДАРТНЫЙ РЕЖИМ
        if (documentType) {
          const existingItem = target.find((item: any) =>
            item.type === documentType || item.singleType === singleType
          )

          if (existingItem) {
            existingItem.img = data.url
          } else {
            target.push({
              type: documentType,
              img: data.url,
              singleType: singleType ?? ''
            })
          }
        } else {
          target.push(data)
        }
        // Pass server URL to parent so image-array fields can store the real URL
        onChange(data.url, e)
      }

      setInputValue(value.replace('C:\\fakepath\\', ''))
    } catch (err) {
      handleErrorUpload(err)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    const errorStatus = imageErrors?.find((e) => e.type === documentType)?.status
    if (errorStatus) setErrorResponse(getTranslateError(errorStatus))
  }, [imageErrors])

  return (
    <div className={cn(styles.fileInputBox, className, (value && valid && !errorResponse) && styles.grayPlaceholder)}>
      <p className={styles.labelText}>
        {content.label[lang]}
        {required && <span className={styles.required}></span>}
      </p>
      <label
        htmlFor={idRef.current}
        className={cn(
          className,
          styles.label,
          !valid || errorResponse ? styles.invalid : '',
          disabled ? styles.disabled : ''
        )}
      >
        <div className={styles.buttonBox}>
          <button className={styles.button}></button>
        </div>
        <span className={styles.valueText}>
          {loading ? (
            <div className={styles.loader_box}>
              <LoadingScreen></LoadingScreen>
              <span>{accountForm.uploading[lang]}...</span>
            </div>
          ) : (
            inputValue
          )}
          {required && <span className={styles.required}></span>}
        </span>
        <input
          data-test-id={props.testName}
          id={idRef.current}
          ref={inputRef}
          disabled={disabled}
          className={`${styles.input}`}
          type="file"
          placeholder={inputValue}
          onChange={handleUpload}
          maxLength={500}
          onBlur={onBlur}
        />
      </label>
      {!valid && content.invalidText ? (
        <span data-test-id={props.testErrorName} className={styles.invalidText}>
          {content.invalidText[lang]}
        </span>
      ) : errorResponse.length > 0 ? (
        <span data-test-id={props.testErrorName} className={styles.invalidText}>
          {errorResponse}
        </span>
      ) : null}
    </div>
  )
}

export default FileInput